import { RoutePath } from "@/routes";
import { ProductListItem } from "@/types";
import { ProductCardLarge } from "@/components/shared/ProductCard";
import { LifecycleStateBadge } from "@/components/shared/LifecycleStateBadge";
import { DeleteHeroAsinTarget } from "@/components/shared/DeleteHeroAsinDialog";
import { AB_TESTS_TAB_PATH, MASTER_LIST_TAB_PATH } from "./productsConstants";

interface ProductsCardViewProps {
  products: ProductListItem[];
  readOnly: boolean;
  /** AUT-363: `subPath` targets a tab under the product; omitted, the card opens its default. */
  onNavigate: (productId: number, subPath?: string) => void;
  onDeleteRequest: (target: DeleteHeroAsinTarget) => void;
  /** Opens the shared set-lifecycle-state dialog for one product (`intent.md § FR-8`). */
  onSetLifecycleRequest: (product: ProductListItem) => void;
}

/**
 * AUT-363 round two. The table view's counts each carry a "View all" link to the tab they
 * counted; the card view showed the same two numbers as plain text with only the card itself
 * clickable, and that click carries no sub-path -- so it opened SEO Strategy. Cards are the
 * default view, so that is what a user actually meets, and it is what QA re-reported against.
 * The counts are now the link, which is the affordance a card has room for.
 */
function ProductMetrics({
  product,
  onNavigate,
}: {
  product: ProductListItem;
  onNavigate: (productId: number, subPath?: string) => void;
}) {
  const hasVisibility = product.visibility_grade != null;
  const abTestsRunning = product.ab_tests_running ?? 0;
  const themesCount = product.themes_count ?? 0;

  // stopPropagation so the count opens its own tab rather than the card's default one.
  const open = (event: React.MouseEvent, subPath: string) => {
    event.stopPropagation();
    onNavigate(product.id, subPath);
  };

  return (
    <div className="mt-2 pt-2 border-t border-border grid grid-cols-3 gap-2 text-xs">
      <div className="flex flex-col">
        <span className="text-muted-foreground text-[11px]">Amazon AI Score</span>
        <span className="font-medium">
          {hasVisibility ? (
            <>
              {product.visibility_grade}
              <span className="text-muted-foreground"> {product.visibility_score}</span>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      </div>
      <div className="flex flex-col">
        <span className="text-muted-foreground text-[11px]">A/B Tests</span>
        {abTestsRunning > 0 ? (
          <button
            type="button"
            className="font-medium text-primary hover:underline text-left"
            onClick={(event) => open(event, AB_TESTS_TAB_PATH)}
          >
            {abTestsRunning}
            <span className="text-muted-foreground"> running</span>
          </button>
        ) : (
          <span className="font-medium">
            0<span className="text-muted-foreground"> running</span>
          </span>
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-muted-foreground text-[11px]">Themes</span>
        {themesCount > 0 ? (
          <button
            type="button"
            className="font-medium text-primary hover:underline text-left"
            onClick={(event) => open(event, MASTER_LIST_TAB_PATH)}
          >
            {themesCount}
          </button>
        ) : (
          <span className="font-medium">0</span>
        )}
      </div>
    </div>
  );
}

export function ProductsCardView({
  products,
  readOnly,
  onNavigate,
  onDeleteRequest,
  onSetLifecycleRequest,
}: ProductsCardViewProps) {
  if (products.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        No products found.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {/*
        `topLeftBadge` gets the raw mirror field, unnormalised here: `LifecycleStateBadge` is the
        normalisation boundary for every rendering reader (`components/product.md`, rule 1).
        `onChangeRequest` rides on the same `readOnly` gate as the kebab item, so the hover key's
        `Change lifecycle state` footer exists on this surface exactly when it exists on the
        table's (`intent.md § FR-8a`) -- and on neither for a viewer, who is passed no callback at
        all and so gets no kebab, while the badge itself still renders (`§ FR-9`).
      */}
      {products.map((product) => (
        <ProductCardLarge
          key={product.id}
          name={product.name}
          imageUrl={product.image_url ?? undefined}
          asin={product.asin}
          nameHref={`${RoutePath.Products}/${product.id}`}
          onClick={() => onNavigate(product.id)}
          onDelete={
            readOnly
              ? undefined
              : () => onDeleteRequest({ id: product.id, name: product.name })
          }
          onSetLifecycleState={readOnly ? undefined : () => onSetLifecycleRequest(product)}
          topLeftBadge={
            <LifecycleStateBadge
              state={product.lifecycle_state}
              onChangeRequest={readOnly ? undefined : () => onSetLifecycleRequest(product)}
            />
          }
        >
          <ProductMetrics product={product} onNavigate={onNavigate} />
        </ProductCardLarge>
      ))}
    </div>
  );
}
